import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FiX } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { HiQuestionMarkCircle } from "react-icons/hi";
import {
  useGetAppraisalObjectivesQuery,
  useGetHrDepartmentsQuery,
  useGetJobRoleQuery,
  useGetRatingsSelectQuery,
  useGetSingleKeyResultAreaQuery,
  useUpdateKRAMutation,
} from "../../../../../api/hrApi";
import DashboardStyle from "../../../../../dashboard/Styles/Dashboard.module.css";
import { ActionButtons } from "../../../../../global/components/Buttons/buttons";
import {
  CheckBox,
  FormInput,
  FormSelect,
  FormTemplate,
  FormTextArea,
} from "../../../../Components/Forms/InputTemplate";
import {
  appraisalPeriod,
  crossReviewType,
  currencies,
  MeasurementType,
} from "./data";
import React, { useEffect } from "react";
import { FormatDateTime } from "../../../../../../utils/functions/ResourceFunctions";

export const EditKRAModal = ({
  setIsOpen,
  isOpen,
  isLoading,
  onValidate,
  id,
}) => {
  const { data } = useGetSingleKeyResultAreaQuery(id);

  const [onEdit] = useUpdateKRAMutation();
  const { data: departments } = useGetHrDepartmentsQuery();
  const formMethods = useForm({
    mode: "all",
    defaultValues: {
      objectiveId: "",
      title: "",
      description: "",
      isFloatable: false,
      // isAutoGenerated: false,
      autoGeneratedSource: "",
      targetValue: "",
      measurementType: "",
      currencyType: "",
      rateId: "",
      appraisalPeriod: "",
      periodStartDate: "",
      // periodEndDate: "",
      slA_StartDuration: "",
      slA_ReviewDuration: "",
      slA_Reminder: "",
      is360Applicable: false,
      includePeerReview: false,
      peerReviewAffectsScore: false,
      includeUpwardReview: false,
      upwardReviewAffectsScore: false,
      includeCrossReview: false,
      crossReviewType: "",
      crossReviewAffectsScore: "",
      jobRoleId: "",
    },
  });
  const {
    handleSubmit,
    formState: { isValid },
    reset,
    control,
  } = formMethods;
  const is360Applicable = useWatch({ control, name: "is360Applicable" });
  const includePeerReview = useWatch({ control, name: "includePeerReview" });
  const watchDepartment = useWatch({ control, name: "departmentId" });
  const includeUpwardReview = useWatch({
    control,
    name: "includeUpwardReview",
  });
  const includeCrossReview = useWatch({ control, name: "includeCrossReview" });
  const watchMeasurementType = useWatch({
    control,
    name: "measurementType",
  });
  const watchRateId = useWatch({ control, name: "rateId" });
  const { data: jobRole } = useGetJobRoleQuery({
    ApprovalStatus: 1,
    departmentId: watchDepartment,
  });
  // Get the current measurement type (from watch or from data if watch is not yet set)
  const currentMeasurementType =
    watchMeasurementType || data?.responseObject?.measurementType;
  const currentRateId = watchRateId || data?.responseObject?.rateId;

  // Fetch ratings data (moved up to avoid initialization order issues)
  const { data: ratings } = useGetRatingsSelectQuery({
    ApprovalStatus: 0,
  });

  React.useEffect(() => {
    if (data?.responseObject) {
      reset({
        title: data.responseObject.title,
        objectiveId: data.responseObject.objectiveId,
        description: data.responseObject.description,
        isFloatable: data.responseObject.isFloatable,
        // isAutoGenerated: data.responseObject.isAutoGenerated,
        autoGeneratedSource: data.responseObject.autoGeneratedSource,
        targetValue: data.responseObject.targetValue,
        measurementType: data.responseObject.measurementType,
        currencyType: data.responseObject.currencyType,
        rateId: data.responseObject.rateId,
        appraisalPeriod: data.responseObject.appraisalPeriod,
        periodStartDate: FormatDateTime(
          data.responseObject.periodStartDate,
          "YYYY-MM-DD"
        ),
        slA_StartDuration: data.responseObject.slaStartDuration,
        slA_ReviewDuration: data.responseObject.slaReviewDuration,
        slA_Reminder: data.responseObject.slaReminderCount,
        is360Applicable: data.responseObject.is360Applicable,
        includePeerReview: data.responseObject.includePeerReview,
        peerReviewAffectsScore: data.responseObject.peerReviewAffectsScore,
        includeUpwardReview: data.responseObject.includeUpwardReview,
        upwardReviewAffectsScore: data.responseObject.upwardReviewAffectsScore,
        includeCrossReview: data.responseObject.includeCrossReview,
        crossReviewType: parseInt(data.responseObject.crossReviewType),
        crossReviewAffectsScore: data.responseObject.crossReviewAffectsScore,
        jobRoleId: data.responseObject.jobRoleId,
        // periodEndDate: FormatDateTime(
        //   data.responseObject.periodEndDate,
        //   "YYYY-MM-DD"
        // ),
        weight: data.responseObject.weight,
        threshold: data.responseObject.threshold,
        departmentId: data?.responseObject?.designation?.departmentId,
      });
    }
  }, [data, reset]);

  // Handle measurementType changes and set targetValue for Yes/No
  useEffect(() => {
    if (watchMeasurementType) {
      const measurementType = MeasurementType.find(
        (type) => type.id === parseInt(watchMeasurementType)
      );
      if (measurementType?.name === "Yes/No") {
        formMethods.setValue("targetValue", "yesNo");
      } else {
        // Clear targetValue for other measurement types to allow user input
        if (measurementType?.name !== "Yes/No") {
          // Only clear if it was previously set to "yesNo"
          const currentValue = formMethods.getValues("targetValue");
          if (currentValue === "yesNo") {
            formMethods.setValue("targetValue", "");
          }
        }
      }
    }
  }, [watchMeasurementType, formMethods]);

  // When 360° is not applicable, clear all its child fields
  useEffect(() => {
    if (!is360Applicable) {
      formMethods.setValue("includePeerReview", false);
      formMethods.setValue("peerReviewAffectsScore", false);
      formMethods.setValue("includeUpwardReview", false);
      formMethods.setValue("upwardReviewAffectsScore", false);
      formMethods.setValue("includeCrossReview", false);
      formMethods.setValue("crossReviewType", 0);
      formMethods.setValue("crossReviewAffectsScore", false);
    }
  }, [is360Applicable, formMethods]);

  // Update threshold when rating is selected (for Rating measurement type)
  useEffect(() => {
    if (
      parseInt(watchMeasurementType) === 4 &&
      watchRateId &&
      parseInt(watchRateId) > 0
    ) {
      // Find the selected rating
      const selectedRating = ratings?.data?.find(
        (rating) => rating.id === parseInt(watchRateId)
      );

      if (selectedRating && selectedRating.scaleLevels) {
        // Find the highest scaleNumber from the scaleLevels
        const highestScale = Math.max(
          ...selectedRating.scaleLevels.map((scale) => scale.scaleNumber)
        );

        // Set the threshold to the highest scale number
        formMethods.setValue("threshold", highestScale);
        console.log(
          "Setting threshold to highest scale:",
          highestScale,
          "for rating:",
          selectedRating.title
        );
      }
    }
  }, [watchMeasurementType, watchRateId, ratings, formMethods]);

  // Fetch filtered objectives for the dropdown options
  const { data: objectives } = useGetAppraisalObjectivesQuery({
    ApprovalStatus: 0,
    departmentId: watchDepartment,
    PageSize: 1000,
    PageNumber: 1,
  });
  const submit = async (sendData) => {
    let processedTargetValue = sendData.targetValue;
    if (parseInt(sendData.measurementType) === 5) {
      processedTargetValue = "yesNo";
    } else if (sendData.targetValue !== "yesNo") {
      processedTargetValue = parseInt(sendData.targetValue) || 0;
    }

    const payload = {
      id,
      objectiveId: parseInt(sendData.objectiveId),
      title: sendData.title,
      description: sendData.description,
      isFloatable: sendData.isFloatable,
      // isAutoGenerated: sendData.isAutoGenerated,
      autoGeneratedSource: sendData.autoGeneratedSource,
      targetValue: processedTargetValue,
      measurementType: parseInt(sendData.measurementType),
      currencyType: sendData.currencyType || "",
      rateId: parseInt(sendData.rateId),
      appraisalPeriod: parseInt(sendData.appraisalPeriod),
      periodStartDate: sendData.periodStartDate,
      // periodEndDate: sendData.periodEndDate,
      slaStartDuration: parseInt(sendData.slA_StartDuration),
      slaReviewDuration: parseInt(sendData.slA_ReviewDuration),
      slaReminderCount: parseInt(sendData.slA_Reminder),
      is360Applicable: sendData.is360Applicable,
      threshold: parseInt(sendData.threshold),
      weight: parseInt(sendData.weight),
      jobRoleId: parseInt(sendData.jobRoleId),
      type: parseInt(data?.responseObject?.type),
    };
    if (sendData.is360Applicable) {
      Object.assign(payload, {
        includePeerReview: sendData.includePeerReview,
        peerReviewAffectsScore: sendData.peerReviewAffectsScore,
        includeUpwardReview: sendData.includeUpwardReview,
        upwardReviewAffectsScore: sendData.upwardReviewAffectsScore,
        includeCrossReview: sendData.includeCrossReview,
        crossReviewType: parseInt(sendData.crossReviewType),
        crossReviewAffectsScore: sendData.crossReviewAffectsScore,
      });
    }
    try {
      const response = await onEdit(payload).unwrap();
      if (response?.successful) {
        setIsOpen(false);
        onValidate();
        toast.success(response?.statusMessage);
      }
    } catch (err) {
      toast.error(err?.data?.statusMessage);
    }
  };

  return (
    <div className={DashboardStyle.dash_board_home_nav}>
      <div className={DashboardStyle.dash_board_home_nav_header}>
        <div>
          <h4 className="text-[25px] font-[500]">Edit</h4>
          <h1 className="text-[28px] font-[800]">key Result Area</h1>
          <p>
            Define measurable outcomes for your selected objectives by creating
            structured KRAs (Key Result Areas)
          </p>
        </div>
        <FiX
          style={{ cursor: "pointer" }}
          size={"1.5rem"}
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>
      <div className={DashboardStyle.dash_board_home_nav_body}>
        {isLoading && <small>Loading...</small>}
        <div>
          <FormProvider {...formMethods}>
            <FormTemplate handleSubmit={handleSubmit(submit)}>
              <FormInput
                title={"Title"}
                camelCase={"title"}
                placeholder={"select"}
              />
              <div className="grid gap-5 grid-cols-2">
                <FormSelect
                  title={"Department"}
                  camelCase={"departmentId"}
                  placeholder={"select"}
                  array={departments?.responseObject?.pageItems?.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt?.name}
                    </option>
                  ))}
                  defaultValue={
                    departments?.responseObject?.pageItems?.find(
                      (opt) =>
                        opt.id ==
                        data?.responseObject?.designation?.departmentId
                    )?.name
                  }
                  defaultId={data?.responseObject?.designation?.departmentId}
                />
                <FormSelect
                  title={"Objective"}
                  camelCase={"objectiveId"}
                  placeholder={"select"}
                  array={[
                    // Add the current objective as the first option if it's not in the filtered list
                    ...(data?.responseObject?.objectiveId &&
                    !objectives?.result?.find(
                      (opt) => opt.id == data?.responseObject?.objectiveId
                    )
                      ? [
                          <option
                            key={data.responseObject.objectiveId}
                            value={data.responseObject.objectiveId}
                          >
                            Selection {data.responseObject.objectiveId}
                          </option>,
                        ]
                      : []),
                    // Add all filtered objectives
                    ...(objectives?.result?.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt?.title} {opt?.designation?.designationName}
                      </option>
                    )) || []),
                  ]}
                  defaultValue={
                    data?.responseObject?.objectiveId &&
                    !objectives?.result?.find(
                      (opt) => opt.id == data?.responseObject?.objectiveId
                    )
                      ? `Current Selection - ID: ${data.responseObject.objectiveId}`
                      : objectives?.result?.find(
                          (opt) => opt.id == data?.responseObject?.objectiveId
                        )?.title
                  }
                  defaultId={data?.responseObject?.objectiveId}
                />
                <FormSelect
                  title={"Job Role"}
                  camelCase={"jobRoleId"}
                  placeholder={"select"}
                  array={jobRole?.responseObject?.pageItems?.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt?.designationName}
                    </option>
                  ))}
                  defaultValue={
                    jobRole?.responseObject?.pageItems?.find(
                      (opt) => opt.id === data?.responseObject?.jobRoleId
                    )?.designationName
                  }
                  defaultId={data?.responseObject?.jobRoleId}
                />
                <FormInput
                  title={"Period Start Date"}
                  camelCase={"periodStartDate"}
                  placeholder={"select"}
                  type={"date"}
                />
                {/* <FormInput
                  title={"Period ENd Date"}
                  camelCase={"periodEndDate"}
                  placeholder={"select"}
                  type={"date"}
                /> */}
                <FormSelect
                  title={"Measurement Type"}
                  camelCase={"measurementType"}
                  placeholder={"select"}
                  array={MeasurementType.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                  defaultValue={
                    MeasurementType.find(
                      (opt) => opt.id === data?.responseObject?.measurementType
                    )?.name
                  }
                  defaultId={data?.responseObject?.measurementType}
                />

                {parseInt(currentMeasurementType) === 2 && (
                  <FormSelect
                    title={"Currency Type"}
                    camelCase={"currencyType"}
                    placeholder={"select"}
                    array={[
                      <option key="empty" value="">
                        Select Currency
                      </option>,
                      ...currencies.map((opt) => (
                        <option key={opt.name} value={opt.name}>
                          {opt.name}
                        </option>
                      )),
                    ]}
                    defaultValue={data?.responseObject?.currencyType || ""}
                  />
                )}

                {parseInt(currentMeasurementType) === 4 && (
                  <FormSelect
                    title={"Rating"}
                    camelCase={"rateId"}
                    placeholder={"select"}
                    array={[
                      <option key="empty" value={0}>
                        Select Rating
                      </option>,
                      ...(ratings?.data?.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.title}
                        </option>
                      )) || []),
                    ]}
                    defaultValue={
                      ratings?.data?.find(
                        (opt) => opt.id === data?.responseObject?.rateId
                      )?.title
                    }
                    defaultId={data?.responseObject?.rateId}
                  />
                )}

                {parseInt(currentMeasurementType) === 5 && (
                  <FormInput
                    title={"Yes/No Value"}
                    camelCase={"targetValue"}
                    placeholder={"Yes/No"}
                    type={"text"}
                    disabled={true}
                    value="yesNo"
                    defaultValue="yesNo"
                  />
                )}
                <FormInput
                  title={"SLA Start Duration"}
                  camelCase={"slA_StartDuration"}
                  placeholder={"select"}
                  type={"number"}
                />
                <FormInput
                  title={"SLA Review Duration"}
                  camelCase={"slA_ReviewDuration"}
                  placeholder={"select"}
                  type={"number"}
                />
                <FormInput
                  title={"SLA Reminder"}
                  camelCase={"slA_Reminder"}
                  placeholder={"select"}
                  type={"number"}
                />

                <FormSelect
                  title={"Appraisal Period"}
                  camelCase={"appraisalPeriod"}
                  placeholder={"select"}
                  array={appraisalPeriod.map((opt) => (
                    <option key={opt.filter} value={opt.filter}>
                      {opt?.name}
                    </option>
                  ))}
                />
                <FormInput
                  title={"Weight"}
                  camelCase={"weight"}
                  placeholder={"select"}
                  type={"number"}
                />
                {(parseInt(currentMeasurementType) === 1 ||
                  parseInt(currentMeasurementType) === 3) && (
                  <FormInput
                    title={"Target Value"}
                    camelCase={"targetValue"}
                    placeholder={"Enter value"}
                    type={"number"}
                    max={
                      parseInt(currentMeasurementType) === 3 ? 100 : undefined
                    }
                    defaultValue={data?.responseObject?.targetValue}
                  />
                )}

                {parseInt(currentMeasurementType) === 2 && (
                  <FormInput
                    title={"Target Value"}
                    camelCase={"targetValue"}
                    placeholder={"Enter currency value"}
                    type={"number"}
                    defaultValue={data?.responseObject?.targetValue}
                  />
                )}

                {parseInt(currentMeasurementType) === 4 && (
                  <FormInput
                    title={"Target Value"}
                    camelCase={"targetValue"}
                    placeholder={"Enter target rating value"}
                    type={"number"}
                    defaultValue={data?.responseObject?.targetValue}
                  />
                )}

                {(parseInt(currentMeasurementType) === 1 ||
                  parseInt(currentMeasurementType) === 3) && (
                  <FormInput
                    title={"Threshold"}
                    camelCase={"threshold"}
                    placeholder={"Enter value"}
                    type={"number"}
                    max={
                      parseInt(currentMeasurementType) === 3 ? 100 : undefined
                    }
                    defaultValue={data?.responseObject?.threshold}
                  />
                )}

                {/* Show threshold for Currency (editable) */}
                {parseInt(currentMeasurementType) === 2 && (
                  <FormInput
                    title={"Threshold"}
                    camelCase={"threshold"}
                    placeholder={"Enter threshold value"}
                    type={"number"}
                    defaultValue={data?.responseObject?.threshold}
                  />
                )}

                {/* Show threshold for Rating (auto-populated from highest scale) */}
                {parseInt(currentMeasurementType) === 4 &&
                  currentRateId &&
                  parseInt(currentRateId) > 0 && (
                    <FormInput
                      title={"Threshold"}
                      camelCase={"threshold"}
                      placeholder={"Auto-populated from rating scale"}
                      type={"number"}
                      disabled={true}
                      defaultValue={data?.responseObject?.threshold}
                    />
                  )}
              </div>
              {/* Basic Options */}
              <div className="grid gap-3 grid-cols-3 mb-4">
                <div className="flex items-center gap-2">
                  <CheckBox camelCase={"isFloatable"} />
                  <small>Is Floatable</small>
                </div>
                {/* <div className="flex items-center gap-2">
                  <CheckBox camelCase={"isAutoGenerated"} />
                  <small>Is Auto Generated</small>
                </div> */}
              </div>

              {/* 360° Appraisal Section */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckBox camelCase={"is360Applicable"} />
                  <small className="font-semibold">Apply 360° Appraisal?</small>
                  <HiQuestionMarkCircle
                    className="text-gray-500 cursor-help"
                    size={16}
                    title="Enable comprehensive feedback from multiple perspectives including peers, subordinates, and cross-functional teams"
                  />
                </div>

                {/* 360° Appraisal Options */}
                <div className="ml-6 border-l-2 border-gray-200 pl-4 space-y-3">
                  {/* Peer Review Section */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckBox
                        camelCase={"includePeerReview"}
                        disabled={!is360Applicable}
                      />
                      <small>Peer Review</small>
                      <HiQuestionMarkCircle
                        className="text-gray-500 cursor-help"
                        size={14}
                        title="Feedback from colleagues at the same level"
                      />
                    </div>
                    <div className="ml-6">
                      <div className="flex items-center gap-2">
                        <CheckBox
                          camelCase={"peerReviewAffectsScore"}
                          disabled={!is360Applicable}
                        />
                        <small>Affects Overall Score?</small>
                      </div>
                    </div>
                  </div>

                  {/* Upward Review Section */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckBox
                        camelCase={"includeUpwardReview"}
                        disabled={!is360Applicable}
                      />
                      <small>Upward Review</small>
                      <HiQuestionMarkCircle
                        className="text-gray-500 cursor-help"
                        size={14}
                        title="Feedback from subordinates – commonly for leadership KRAs"
                      />
                    </div>
                    <div className="ml-6">
                      <div className="flex items-center gap-2">
                        <CheckBox
                          camelCase={"upwardReviewAffectsScore"}
                          disabled={!is360Applicable}
                        />
                        <small>Affects Overall Score?</small>
                      </div>
                    </div>
                  </div>

                  {/* Cross Review Section */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckBox
                        camelCase={"includeCrossReview"}
                        disabled={!is360Applicable}
                      />
                      <small>Cross Review</small>
                      <HiQuestionMarkCircle
                        className="text-gray-500 cursor-help"
                        size={14}
                        title="Review from other units or departments"
                      />
                    </div>
                    <div className="ml-6 space-y-2">
                      <div className="grid gap-3 grid-cols-1">
                        <FormSelect
                          title={"Specify Type"}
                          camelCase={"crossReviewType"}
                          placeholder={"select"}
                          disabled={!is360Applicable}
                          array={crossReviewType.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                          isOptional={true}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckBox
                          camelCase={"crossReviewAffectsScore"}
                          disabled={!is360Applicable}
                        />
                        <small>Affects Overall Score?</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FormTextArea
                className="h-[200px]"
                title={"Description"}
                camelCase={"description"}
              />
              <div
                style={{ marginTop: "1rem" }}
                className={DashboardStyle.button_cage}
              >
                <ActionButtons
                  isLoading={isLoading}
                  disabled={false}
                  width={"auto"}
                  className={DashboardStyle?.button_cage_weight}
                  bg="var(--primary-color)"
                >
                  Update
                </ActionButtons>
              </div>
            </FormTemplate>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
